import { AccordionSubFilterItem } from "../../../accordion/Accordion";
import { useContext } from "react";
import { stringQueries } from "../../../../config/filter/CustomFilterConfig";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  PersonFilterKeys,
  PersonVejnavnFilterSetting,
  QueryFilter,
} from "../../../../interfaces/filters";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { PersonDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import useVejnavnAutoComplete from "../../../../hooks/useVejnavnAutoComplete";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import SearchIcon from "../../../icons/SearchIcon";
import Spinner from "../../../spinner/Spinner";
import { useCombobox } from "downshift";

// helper
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "vejnavn";

export default function PersonVejnavnFilter() {
  // query filter context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find the filter settings
  const settings: PersonVejnavnFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // preppings
  const selectedQuery = stringQueries[0];

  // vejnavn autocomplete hook
  const { data, isPending, debouncedInputValue, setQuery } =
    useVejnavnAutoComplete();

  // handlers
  function inputEventHandler(inputValue: string) {
    if (!inputValue) {
      return;
    }

    const key: PersonVejnavnFilterSetting["key"] = `${selectedQuery.query}-${inputValue}`;

    if (settings.some((set) => set.key === key)) {
      alert("Filter er allerede aktivt.");
      return;
    }

    const settingsItem: PersonVejnavnFilterSetting = {
      key: key,
      selectedQueryName: `${selectedQuery.displayName}`,
      displayName: `${inputValue}`,
      filter: {
        adresse: {
          adresse: {
            adresselinie1: {
              [selectedQuery.query]: inputValue,
            },
          },
        },
      },
    };

    const updatedSettings = [...settings, settingsItem];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonVejnavnFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(key: PersonVejnavnFilterSetting["key"]) {
    const updatedSettings = settings.filter((set) => set.key !== key);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<PersonDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonVejnavnFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  return (
    <AccordionSubFilterItem
      title="Vejnavn"
      value="vejnavn"
      isActive={!!settings.length}
    >
      <div className="c-applied-filter-group">
        {settings.map((set: PersonVejnavnFilterSetting) => {
          return (
            <AppliedFilterItem
              key={set.key}
              labelPrefix={set.selectedQueryName}
              label={set.displayName}
              removeFilterHandler={() => {
                removeFilterEventHandler(set.key);
              }}
            />
          );
        })}
      </div>

      <SuperDropdown
        items={data}
        itemsIdKey={"id"}
        itemToString={(item) => item?.vejnavn.navn ?? ""}
        placeholder="Søg efter vejnavne ..."
        asDropdown={false}
        initialInputValue={debouncedInputValue}
        clearInputAfterSelection={true}
        closedIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
        openIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
        itemToElement={(item) => (
          <DefaultDropdownItem label={item.vejnavn.navn} />
        )}
        onStateChange={(changes) => {
          if (changes.inputValue !== undefined) {
            const inputValue = changes.inputValue || "";
            setQuery(inputValue);
          }
        }}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem) {
            inputEventHandler(item.selectedItem?.vejnavn.navn);
          }
        }}
        stateReducer={(_state, actionAndChanges) => {
          const { type, changes } = actionAndChanges;
          switch (type) {
            case useCombobox.stateChangeTypes.InputKeyDownEnter:
              if (changes.inputValue) {
                inputEventHandler(changes.inputValue);
              }
              return {
                ...changes,
                inputValue: "",
              };
          }
          return { ...changes };
        }}
      />
    </AccordionSubFilterItem>
  );
}
